import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Plus, Edit2, Trash2, Briefcase, Users, Star } from 'lucide-react';
import { toast } from 'sonner';
import { sortCommittees, sortMembers } from '../sortUtils';

const empty = () => ({ name: '', charter: '', chair_member_id: '', member_ids: [] });

export default function CommitteesTab({ api, isAdmin, members }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get('/board/committees'); setList(data); } catch (e) { /* noop */ }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const memberName = (id) => members.find(m => m.id === id)?.name || '—';
  const memberById = (id) => members.find(m => m.id === id);
  const sortedList = React.useMemo(() => sortCommittees(list), [list]);
  const openCreate = () => { setEditing(null); setForm(empty()); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...empty(), ...c }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    setBusy(true);
    try {
      if (editing) await api.put(`/board/committees/${editing.id}`, form);
      else await api.post('/board/committees', form);
      toast.success('Saved'); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setBusy(false);
  };
  const del = async (c) => {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    try { await api.delete(`/board/committees/${c.id}`); toast.success('Deleted'); load(); } catch (e) { toast.error('Failed'); }
  };
  const toggleMember = (mid) => {
    setForm(f => {
      const has = f.member_ids.includes(mid);
      return { ...f, member_ids: has ? f.member_ids.filter(x => x !== mid) : [...f.member_ids, mid] };
    });
  };

  return (
    <div className="space-y-4" data-testid="committees-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-[#2D1B4E]">Committees</h2>
          <p className="text-xs text-[#5A4D6E]">{sortedList.length} committee{sortedList.length !== 1 ? 's' : ''} · sorted alphabetically</p>
        </div>
        {isAdmin && <Button onClick={openCreate} className="bg-[#6B3FA0]" data-testid="add-committee-btn"><Plus className="h-4 w-4 mr-1" /> Add Committee</Button>}
      </div>

      {sortedList.length === 0 ? (
        <p className="text-center py-12 text-[#8E85A0] text-sm">No committees yet.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {sortedList.map(c => {
            const chair = c.chair_member_id ? memberById(c.chair_member_id) : null;
            // Chair pinned first, then other members alphabetically
            const otherMemberIds = (c.member_ids || []).filter(mid => mid !== c.chair_member_id);
            const otherMembers = sortMembers(otherMemberIds.map(memberById).filter(Boolean));
            return (
            <Card key={c.id} className="border-[#DED8EB] bg-white" data-testid={`committee-${c.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2"><Briefcase className="h-4 w-4 text-[#43A047]" />
                    <p className="font-bold text-[#2D1B4E]">{c.name}</p>
                    <Badge className="text-[10px] bg-[#F0EDF6] text-[#6B3FA0] border-[#DED8EB]">
                      <Users className="h-2.5 w-2.5 inline mr-0.5" />
                      {(c.member_ids || []).length}
                    </Badge>
                  </div>
                  {isAdmin && (
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(c)} className="h-7 w-7"><Edit2 className="h-3 w-3" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => del(c)} className="h-7 w-7 text-[#D9534F]"><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  )}
                </div>
                {chair && (
                  <div className="mt-2 flex items-center gap-1.5">
                    <Star className="h-3 w-3 text-[#FFB74D] fill-[#FFB74D]" />
                    <span className="text-xs text-[#5A4D6E]">Chair: <strong className="text-[#2D1B4E]">{chair.name}</strong></span>
                  </div>
                )}
                {c.charter && <p className="text-xs italic text-[#8E85A0] mt-2">{c.charter}</p>}
                {otherMembers.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {otherMembers.map(m => <Badge key={m.id} className="text-[10px] bg-[#EDE7F6] text-[#6B3FA0]">{m.name}</Badge>)}
                  </div>
                )}
              </CardContent>
            </Card>
          );})}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'Add'} Committee</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Name *</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
            <div><Label className="text-xs">Charter / Purpose</Label>
              <textarea value={form.charter} onChange={e => setForm(f => ({ ...f, charter: e.target.value }))} rows={3} className="w-full border border-[#DED8EB] rounded px-2 py-1 text-sm" />
            </div>
            <div><Label className="text-xs">Chair</Label>
              <select value={form.chair_member_id} onChange={e => setForm(f => ({ ...f, chair_member_id: e.target.value }))} className="w-full border border-[#DED8EB] rounded px-2 py-1.5 text-sm bg-white">
                <option value="">— none —</option>
                {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Members</Label>
              <div className="max-h-44 overflow-y-auto border border-[#DED8EB] rounded p-2 space-y-1">
                {members.map(m => (
                  <label key={m.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={form.member_ids.includes(m.id)} onChange={() => toggleMember(m.id)} />
                    {m.name}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-[#6B3FA0]">{busy ? 'Saving…' : 'Save'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
